<template>
  <div
    class="reviews"
    :class="{ 'reviews--desktop': userDevice.innerWidth >= desktopBreakPoint }">
    <div class="reviews__header">
      <h3
        v-if="sliderTitle"
        class="reviews__header-title">
        {{ sliderTitle }}
      </h3>

      <slider-controls
        v-if="isEnoughReviewsForSlider && wasInitGlide"
        class="reviews__header-controls"
        @prevSlide="glide.go('<')"
        @nextSlide="glide.go('>')" />
    </div>

    <div
      v-if="!isDesktopView"
      class="reviews__body scrollbar-hidden">
      <review-item
        v-for="item in reviews"
        :key="item.id || item.text"
        v-bind="propsForReviewItem"
        class="reviews__slide"
        :item="item" />
    </div>

    <template
      v-else-if="isReady && isEnoughReviewsForSlider">
      <div
        v-show="userDevice.innerWidth >= desktopBreakPoint"
        class="reviews-slider__glide-container">
        <div
          class="glide__track"
          data-glide-el="track">
          <div class="glide__slides">
            <review-item
              v-for="(item, idx) in reviews"
              :key="idx"
              v-bind="propsForReviewItem"
              class="glide__slide"
              :item="item" />
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import ReviewItem from 'Components/Reviews/ReviewItem.vue';
import SliderControls from 'Components/SliderControls.vue';

export default {
  name: 'ReviewsSlider',

  components: {
    SliderControls,
    ReviewItem,
  },

  props: {
    reviews: {
      type: Array,
      default: () => ([]),
    },

    perView: {
      type: [Number, String],
      default: 1,
    },

    breakpoints: {
      type: Object,
      default: () => ({}),
    },

    propsForReviewItem: {
      type: Object,
      default: () => ({}),
    },

    sliderTitle: {
      type: String,
      default: '',
    },

    desktopBreakPoint: {
      type: Number,
      default: 1024,
    },

    product: {
      type: Object,
      default: null,
    },

    minReviewsForSlider: {
      type: Number,
      default: 4,
    },
  },

  data() {
    return {
      glide: null,
      isReady: false,
      wasInitGlide: false,
      trustpilotLink: 'https://www.trustpilot.com/review/skycoach.gg?sort=recency&stars=5',
    };
  },

  computed: {
    ...mapState(['userDevice']),
    ...mapState('products', ['headerInfo']),
    ...mapGetters(['isPlatformApp']),

    isEnoughReviewsForSlider() {
      return this.reviews.length >= this.minReviewsForSlider;
    },

    isDesktopView() {
      return this.userDevice.innerWidth >= this.desktopBreakPoint && this.isEnoughReviewsForSlider;
    },
  },

  watch: {
    'userDevice.innerWidth': {
      handler(newVal) {
        if (newVal < this.desktopBreakPoint) {
          this.destroyGlide();
          return;
        }

        if (this.isReady && !this.wasInitGlide && this.isEnoughReviewsForSlider) {
          this.initGlide();
        }
      },
    },
  },

  mounted() {
    this.$nextTick(() => {
      this.isReady = true;

      if (this.userDevice.innerWidth >= this.desktopBreakPoint && this.isEnoughReviewsForSlider) {
        this.initGlide();
      }
    });
  },

  beforeDestroy() {
    this.destroyGlide();
  },

  methods: {
    initGlide() {
      if (this.wasInitGlide || !this.isEnoughReviewsForSlider) {
        return;
      }

      const GlideImport = () => import('@glidejs/glide');
      this.wasInitGlide = true;

      GlideImport()
        .then((data) => {
          const Glide = data.default;

          this.glide = new Glide('.reviews-slider__glide-container', {
            type: 'carousel',
            perView: +this.perView,
            gap: 16,
            breakpoints: this.breakpoints,
            swipeThreshold: false,
            dragThreshold: false,
          }).mount();

          this.wasInitGlide = true;
        }).catch((error) => {
          console.error('Error initializing Glide:', error);
          this.wasInitGlide = false;
        });
    },

    destroyGlide() {
      if (this.glide) {
        this.glide.destroy();
        this.glide = null;
      }

      this.wasInitGlide = false;
    },
  },
};
</script>

<style lang="scss" scoped>
  .reviews {
    width: 100%;
    margin: 0 auto;
  }

  .reviews__header {
    margin-bottom: 24px;
  }

  .reviews__header-title {
    font-weight: 500;
    font-size: $text-25px;
    line-height: normal;
  }

  .reviews__slide:last-child:after {
    content: '';
    display: block;
    position: absolute;
    height: 1px;
    width: 6vw;
    right: -6vw;

    @media screen and #{$screen-more-or-640} {
      right: -3vw;
      width: 3vw;
    }
  }

  .reviews:not(.reviews--desktop) {
    .reviews__header-controls {
      display: none;
    }
  }

  .reviews__body {
    display: grid;
    grid-auto-flow: column;
    grid-auto-columns: min-content;
    gap: 0 13px;

    overflow-x: scroll;
    overflow-y: hidden;
    scroll-snap-type: x mandatory;

    scroll-behavior: smooth;
    -webkit-overflow-scrolling: touch;
  }

  // переход на десктоп не через media
  // так как не везде переход на 1024, на деталке 1200 напр
  .reviews.reviews--desktop {
    .reviews__header {
      display: flex;
      align-items: center;
      justify-content: space-between;
    }

    .reviews__header-content {
      flex: 1;
    }

    .reviews__header-controls {
      margin-left: 10px;
    }
  }

  .reviews-slider__glide-container {
    width: 100%;
  }
</style>
